/**
 * Learner sign-in, on the prototype's split-panel layout.
 *
 * Provider buttons come from the tenant's own third_party_auth rows, so two academies
 * on the same platform can offer entirely different SSO options with no code change.
 *
 * Password login uses the v2 endpoint deliberately: v1 requires a field literally named
 * `email` and matches by email only, so posting a username to it fails for everyone
 * with "There was an error receiving your login information."
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banner, Button, Field } from '../../shared/ui';
import { getConfig, text } from '../../shared/lib/config';
import { signInWithPassword } from '../../shared/lib/session';
import { loadAuthContext, EMPTY_AUTH_CONTEXT, type AuthContext } from '../../shared/lib/providers';
import { ProviderButtons } from './ProviderButtons';
import './auth.css';

export default function Login() {
  const config = getConfig();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/dashboard';

  const [auth, setAuth] = useState<AuthContext>(EMPTY_AUTH_CONTEXT);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadAuthContext(next).then(setAuth);
  }, [next]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithPassword(identifier, password);
      window.location.assign(next);
    } catch (err) {
      const status = (err as { status?: number }).status;
      setError(
        status === 400 || status === 403
          ? 'That email or username and password did not match an account.'
          : 'Sign-in is unavailable right now. Please try again shortly.',
      );
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <aside className="auth__visual">
        <div className="auth__logo">{config.tenant.name}</div>
        <div className="auth__pitch">
          <h2>{text('hero_heading', 'Learn something new')}</h2>
          <p>{text('hero_body', '')}</p>
        </div>
      </aside>

      <section className="auth__pane">
        <div className="auth__card">
          <p className="auth__eyebrow">Welcome back</p>
          <h1 className="auth__title">Sign in</h1>

          {/* Surfaced from the pipeline: a failed SSO attempt lands back here with the
              reason, and swallowing it leaves the learner with no idea what happened. */}
          {auth.errorMessage && <Banner tone="danger">{auth.errorMessage}</Banner>}
          {error && <Banner tone="danger">{error}</Banner>}

          <form className="auth__form" onSubmit={submit} noValidate>
            <Field
              label="Email or username"
              name="identifier"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
            />
            <Field
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" variant="primary" busy={busy}>
              Sign in
            </Button>
          </form>

          <ProviderButtons providers={auth.providers} mode="login" />

          {config.auth.allowNativeRegistration && (
            <p className="auth__alt">
              New here? <Link to={`/register?next=${encodeURIComponent(next)}`}>Create an account</Link>
            </p>
          )}
        </div>
      </section>
    </main>
  );
}
